import { Stack, TextField, SwipeableDrawer, Box, Typography, Button, Chip, useMediaQuery, useTheme } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckIcon from '@mui/icons-material/Check';
import React from 'react'
import { GridLogicOperator, gridFilterModelSelector, gridSortedRowEntriesSelector, useGridApiContext } from '@mui/x-data-grid-pro';
import { getMultiPositionsToSinglePosition } from '../helpers/MultiPositionsToSinglePosition';

const positionSortOrder: Record<string, number> = {
  // MLB
  P: 1,
  C: 2,
  '1B': 3,
  '2B': 4,
  '3B': 5,
  'SS': 6,
  'OF': 7,
  // NBA - Decimals because C exists in both MLB and NBA
  'PG': .1,
  'SG': .2,
  'SF': .3,
  'PF': .4,
  // NFL
  'QB': 0.01,
  'RB': 0.02,
  'WR': 0.03,
  'TE': 0.04,
  'DST': 0.05,
  'K': 0.06,
  'FLEX': 0.07,
}


const QuickFilterToolbar = ({ reverse }: { reverse: boolean }) => {
  const [selected, setSelected] = React.useState<string>('')
  const [search, setSearch] = React.useState<string>('')
  const [drawerOpen, setDrawerOpen] = React.useState(false)
  const apiRef = useGridApiContext();
  const currentFilterModel = gridFilterModelSelector(apiRef);
  const rows = gridSortedRowEntriesSelector(apiRef);
  const positions = rows.map((row) => row.model.position);
  const singlePositions = getMultiPositionsToSinglePosition(positions).sort((a, b) => (positionSortOrder[a] ?? 99) - (positionSortOrder[b] ?? 99));
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const onSearch = (search: string) => {
    apiRef.current.setFilterModel({
      items: [
        ...currentFilterModel.items.filter((item) => item.field !== 'name').filter((item) => item.field !== 'player'),
        { field: 'name', operator: 'contains', value: search },
        { field: 'player', operator: 'contains', value: search },
      ]
    })
    setSearch(search);
  }

  const onSelect = (newSelection: string) => {
    apiRef.current.setFilterModel({
      logicOperator: GridLogicOperator.Or,
      items: [
        ...currentFilterModel.items.filter((item) => item.field !== 'position'),
        ...newSelection && newSelection !== selected ? [{ field: 'position', operator: 'contains', value: newSelection }] : []
      ],
    })
    setSelected(newSelection !== selected ? newSelection : '');
  }

  const handleDrawerOpen = () => setDrawerOpen(true);
  const handleDrawerClose = () => setDrawerOpen(false);

  const handlePositionSelect = (position: string) => {
    onSelect(position);
    handleDrawerClose();
  }

  // Mobile view with bottom drawer
  if (isMobile) {
    return (
      <>
        <Stack direction={reverse ? 'row-reverse' : 'row'} alignItems='center' columnGap={2} width="-webkit-fill-available">
          <Chip
            label={selected || 'All'}
            variant='outlined'
            onClick={handleDrawerOpen}
            sx={{
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '24px',
              p: '8px 12px',
              color: 'white'
            }}
          />
          <TextField
            variant='outlined'
            placeholder='Search Players'
            InputProps={{
              startAdornment: <SearchIcon />,
            }}
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            sx={{
              flex: 1,
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '6px',
              color: 'white',
              '& .MuiInputBase-root': {
                paddingLeft: '8px',
              },
              '& input': {
                color: 'white',
                padding: 0,
                paddingLeft: '8px',
                height: '32px',
              }
            }}
          />
          
        </Stack>

        <SwipeableDrawer
          anchor="bottom"
          open={drawerOpen}
          onClose={handleDrawerClose}
          onOpen={handleDrawerOpen}
          disableSwipeToOpen
          sx={{ zIndex: 1400 }}
          PaperProps={{
            sx: {
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              px: 2,
              pt: 1,
              pb: 2,
              backgroundColor: '#1A1A2E',
            }
          }}
        >
          {/* Grabber Handle */}
          <Box
            sx={{
              width: '40px',
              height: '4px',
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              margin: '8px auto 16px',
            }}
          />

          {/* Header */}
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
            Filter Positions
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 2 }}>
            Select positions to display in the table
          </Typography>

          {/* Position Chips Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1.5,
              mb: 3,
            }}
          >
            <Chip
              label="ALL"
              icon={!selected ? <CheckIcon sx={{ fontSize: 16 }} /> : undefined}
              onClick={() => handlePositionSelect('')}
              sx={{
                height: '44px',
                fontSize: '14px',
                fontWeight: 500,
                backgroundColor: !selected ? '#015ACE' : 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                justifyContent: 'center',
                '& .MuiChip-icon': {
                  color: 'white',
                },
                '& .MuiChip-label': {
                  px: !selected ? 0.5 : 2,
                },
                '&:hover': {
                  backgroundColor: !selected ? '#0149B0' : 'rgba(255, 255, 255, 0.15)',
                }
              }}
            />
            {singlePositions.map((position) => (
              <Chip
                key={position}
                label={position}
                icon={selected === position ? <CheckIcon sx={{ fontSize: 16 }} /> : undefined}
                onClick={() => handlePositionSelect(position)}
                sx={{
                  height: '44px',
                  fontSize: '14px',
                  fontWeight: 500,
                  backgroundColor: selected === position ? '#015ACE' : 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  justifyContent: 'center',
                  '& .MuiChip-icon': {
                    color: 'white',
                  },
                  '& .MuiChip-label': {
                    px: selected === position ? 0.5 : 2,
                  },
                  '&:hover': {
                    backgroundColor: selected === position ? '#0149B0' : 'rgba(255, 255, 255, 0.15)',
                  }
                }}
              />
            ))}
          </Box>
        </SwipeableDrawer>
      </>
    );
  }

  // Desktop view (unchanged)
  return (
    <Stack direction={reverse ? 'row-reverse' : 'row'} alignItems='center' columnGap={2}>
      <TextField
        variant='outlined'
        placeholder='Search Players'
        InputProps={{
          startAdornment: <SearchIcon />,
        }}
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        sx={{
          border: '1px solid rgba(255, 255, 255, 0.15)',
          borderRadius: '6px',
          color: 'white',
          '& .MuiInputBase-root': {
            paddingLeft: '8px',
          },
          '& input': {
            color: 'white',
            padding: 0,
            paddingLeft: '8px',
            height: '32px',
          }
        }}
      />
      <Stack direction='row' columnGap={2.5}>
        <Stack fontSize='12px' fontWeight={500} padding='8px' onClick={() => onSelect('')} sx={{ cursor: 'pointer', ...(!selected && { backgroundColor: '#015ACE', borderRadius: '6px', }) }}>ALL</Stack>
        {
          singlePositions.map((position) => (
            <Stack key={position} fontSize='12px' fontWeight={500} padding='8px' onClick={() => onSelect(position)} sx={{ cursor: 'pointer', ...(selected === position && { backgroundColor: '#015ACE', borderRadius: '6px', }) }}>{position}</Stack>
          ))
        }
      </Stack>
    </Stack>
  )
}

export default QuickFilterToolbar